import {
  Al,
  At,
  Au,
  Bg,
  Br,
  Ca,
  Ch,
  Cn,
  Cs,
  De,
  Dk,
  Es,
  Fi,
  Fr,
  Gb,
  Gr,
  Hu,
  Ie,
  In,
  Int,
  It,
  Md,
  Nl,
  No,
  Nz,
  Pl,
  Pt,
  Ro,
  Ru,
  Se,
  Tr,
  Us,
} from "@airhelp/flags";
import type { FlagProps } from "components/Flags";
import config from "config/config";

const LANGUAGES = [
  "al",
  "bg",
  "cs",
  "da",
  "de",
  "de-at",
  "de-ch",
  "en",
  "en-au",
  "en-ca",
  "en-gb",
  "en-in",
  "en-int",
  "en-ie",
  "en-nz",
  "es",
  "el",
  "fr",
  "fr-ca",
  "hu",
  "it",
  "nl",
  "nb",
  "pl",
  "pt-pt",
  "pt-br",
  "ru",
  "ro",
  "ro-md",
  "fi",
  "sv",
  "zh-cn",
  "tr",
  "en-default",
  "fr-default",
  "de-default",
] as const;

export type LanguageCode = typeof LANGUAGES[number];

export type DefaultLanguageCode = "en-default" | "fr-default" | "de-default";

export const DEFAULT_FALLBACK_LANGUAGE: LanguageCode = "en-int";

export const alternateLanguages: Record<DefaultLanguageCode, LanguageCode[]> = {
  "en-default": ["en", "en-au", "en-ca", "en-gb", "en-in", "en-int", "en-ie", "en-nz"],
  "fr-default": ["fr", "fr-ca"],
  "de-default": ["de", "de-at", "de-ch"],
};

export type Language = {
  name: string;
  hreflang: string;
  hrefsToTranslation: string;
  languageFlag: FlagProps["flag"];
  popularLanguage?: boolean;
  hidden?: boolean;
  localDomain?: string;
  oneTrustDomainId?: string;
};

export default new Map<LanguageCode, Language>(
  Object.entries<Language>({
    al: {
      name: "Ardit",
      hreflang: "sq-AL",
      hrefsToTranslation: "sq_al",
      languageFlag: Al,
      hidden: true,
    },
    bg: {
      name: "Български",
      hreflang: "bg-BG",
      hrefsToTranslation: "bg_bg",
      languageFlag: Bg,
      hidden: true,
    },
    cs: {
      name: "Čeština",
      hreflang: "cs-CZ",
      hrefsToTranslation: "cs_cz",
      languageFlag: Cs,
      hidden: true,
    },
    da: {
      name: "Dansk",
      hreflang: "da-DK",
      hrefsToTranslation: "da_dk",
      languageFlag: Dk,
    },
    de: {
      name: "Deutsch",
      hreflang: "de-DE",
      hrefsToTranslation: "de_de",
      languageFlag: De,
    },
    "de-at": {
      name: "Deutsch (Österreich)",
      hreflang: "de-AT",
      hrefsToTranslation: "de_de",
      languageFlag: At,
    },
    "de-ch": {
      name: "Deutsch (Schweiz)",
      hreflang: "de-CH",
      hrefsToTranslation: "de_de",
      languageFlag: Ch,
    },
    en: {
      name: "English (USA)",
      hreflang: "en-US",
      hrefsToTranslation: "en_us",
      languageFlag: Us,
      popularLanguage: true,
    },
    "en-au": {
      name: "English (Australia)",
      hreflang: "en-AU",
      hrefsToTranslation: "en_us",
      languageFlag: Au,
      hidden: true,
    },
    "en-ca": {
      name: "English (Canada)",
      hreflang: "en-CA",
      hrefsToTranslation: "en_us",
      languageFlag: Ca,
      localDomain: config.domainEnCa,
      oneTrustDomainId: config.oneTrustDomainIdCa,
    },
    "en-gb": {
      name: "English (UK)",
      hreflang: "en-GB",
      hrefsToTranslation: "en_gb",
      languageFlag: Gb,
      popularLanguage: true,
    },
    "en-in": {
      name: "English (India)",
      hreflang: "en-IN",
      hrefsToTranslation: "en_us",
      languageFlag: In,
      hidden: true,
    },
    "en-int": {
      name: "English (International)",
      hreflang: "x-default",
      hrefsToTranslation: "en_001",
      languageFlag: Int,
    },
    "en-ie": {
      name: "English (Ireland)",
      hreflang: "en-IE",
      hrefsToTranslation: "en_us",
      languageFlag: Ie,
    },
    "en-nz": {
      name: "English (New Zealand)",
      hreflang: "en-NZ",
      hrefsToTranslation: "en_us",
      languageFlag: Nz,
      hidden: true,
    },
    es: {
      name: "Español",
      hreflang: "es-ES",
      hrefsToTranslation: "es_es",
      languageFlag: Es,
    },
    el: {
      name: "Eλληνικα",
      hreflang: "el-GR",
      hrefsToTranslation: "el_gr",
      languageFlag: Gr,
    },
    fr: {
      name: "Français",
      hreflang: "fr-FR",
      hrefsToTranslation: "fr_fr",
      languageFlag: Fr,
      popularLanguage: true,
    },
    "fr-ca": {
      name: "Français (Canada)",
      hreflang: "fr-CA",
      hrefsToTranslation: "fr_fr",
      languageFlag: Ca,
    },
    hu: {
      name: "Magyar",
      hreflang: "hu-HU",
      hrefsToTranslation: "hu_hu",
      languageFlag: Hu,
      hidden: true,
    },
    it: {
      name: "Italiano",
      hreflang: "it-IT",
      hrefsToTranslation: "it_it",
      languageFlag: It,
    },
    nl: {
      name: "Nederlands",
      hreflang: "nl-NL",
      hrefsToTranslation: "nl_nl",
      languageFlag: Nl,
    },
    nb: {
      name: "Norsk",
      hreflang: "nb-NO",
      hrefsToTranslation: "nb_no",
      languageFlag: No,
    },
    pl: {
      name: "Polski",
      hreflang: "pl-PL",
      hrefsToTranslation: "pl_pl",
      languageFlag: Pl,
    },
    "pt-br": {
      name: "Português (Brasil)",
      hreflang: "pt-BR",
      hrefsToTranslation: "pt_br",
      languageFlag: Br,
    },
    "pt-pt": {
      name: "Português (Portugal)",
      hreflang: "pt-PT",
      hrefsToTranslation: "pt_pt",
      languageFlag: Pt,
      popularLanguage: true,
    },
    ru: {
      name: "Pусский",
      hreflang: "ru-RU",
      hrefsToTranslation: "ru_ru",
      languageFlag: Ru,
    },
    ro: {
      name: "Română",
      hreflang: "ro-RO",
      hrefsToTranslation: "ro_ro",
      languageFlag: Ro,
    },
    "ro-md": {
      name: "молдовеняскэ",
      hreflang: "ro-MD",
      hrefsToTranslation: "ro_md",
      languageFlag: Md,
      hidden: true,
    },
    fi: {
      name: "Suomi",
      hreflang: "fi-FI",
      hrefsToTranslation: "fi_fi",
      languageFlag: Fi,
    },
    sv: {
      name: "Svenska",
      hreflang: "sv-SE",
      hrefsToTranslation: "sv_se",
      languageFlag: Se,
    },
    "zh-cn": {
      name: "汉语",
      hreflang: "zh-CN",
      hrefsToTranslation: "zh_cn",
      languageFlag: Cn,
    },
    tr: {
      name: "Türkçe",
      hreflang: "tr-TR",
      hrefsToTranslation: "tr_tr",
      languageFlag: Tr,
    },
  }) as [LanguageCode, Language][]
);
